import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut, useAuth, useClerk } from '@clerk/clerk-react';
import { QueryProvider } from './providers/QueryProvider';
import { Layout } from './components/Layout';
import { SetupWizard } from './components/setup/SetupWizard';
import { SignIn } from './pages/SignIn';
import { SignUp } from './pages/SignUp';
import { Home } from './pages/Home';
import { StoreFinder } from './pages/StoreFinder';
import { MyStores } from './pages/MyStores';
import { RevenueEntry } from './pages/RevenueEntry';
import { Schedule } from './pages/Schedule';
import { DailyRoute } from './pages/DailyRoute';
import { MarketsAndDeals } from './pages/MarketsAndDeals';
import { Settings } from './pages/Settings';
import { Admin } from './pages/Admin';
import { setAuthTokenGetter } from './services/api';

const SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 hours
const LAST_ACTIVE_KEY = 'ts_last_active';

function AuthTokenInitializer({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const { signOut } = useClerk();

  // Set synchronously during render so child queries on initial mount
  // have a token getter available before their effects fire.
  setAuthTokenGetter(async () => {
    try {
      return await getToken();
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const checkAndRefresh = () => {
      const last = localStorage.getItem(LAST_ACTIVE_KEY);
      const now = Date.now();
      if (last && now - parseInt(last) > SESSION_TIMEOUT_MS) {
        localStorage.removeItem(LAST_ACTIVE_KEY);
        signOut();
        return;
      }
      localStorage.setItem(LAST_ACTIVE_KEY, now.toString());
    };

    // Check on mount
    checkAndRefresh();

    // Check whenever the tab becomes visible again
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndRefresh();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [signOut]);

  return <>{children}</>;
}

function SetupGuard({ children }: { children: React.ReactNode }) {
  const isSetupComplete = localStorage.getItem('setup_complete') === 'true';
  if (!isSetupComplete) return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

function ProtectedRoutes() {
  return (
    <>
      <SignedIn>
        <AuthTokenInitializer>
          <Routes>
            <Route path="/setup" element={<SetupWizard />} />

            <Route
              path="/"
              element={
                <SetupGuard>
                  <Layout />
                </SetupGuard>
              }
            >
              <Route index element={<Home />} />
              <Route path="stores/find" element={<StoreFinder />} />
              <Route path="stores" element={<MyStores />} />
              <Route path="revenue" element={<RevenueEntry />} />
              <Route path="schedule" element={<Schedule />} />
              <Route path="route/:date" element={<DailyRoute />} />
              <Route path="deals" element={<MarketsAndDeals />} />
              <Route path="markets" element={<MarketsAndDeals />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<Admin />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthTokenInitializer>
      </SignedIn>

      <SignedOut>
        <Routes>
          <Route path="/sign-in/*" element={<SignIn />} />
          <Route path="/sign-up/*" element={<SignUp />} />
          <Route path="*" element={<Navigate to="/sign-in" replace />} />
        </Routes>
      </SignedOut>
    </>
  );
}

function App() {
  return (
    <QueryProvider>
      <BrowserRouter>
        <ProtectedRoutes />
      </BrowserRouter>
    </QueryProvider>
  );
}

export default App;
